import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import React from "react";
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import { ModeInsightsProps } from "../../types/insights";
import { yearsAndMonths } from "../../utils";
import ModeInsightsCard from "./ModeInsightsCard";
import StarIcon from "./StarIcon";

export default function MonthlyPaymentInsights({ model }: ModeInsightsProps) {
  const c = useCurrencyFormatter();
  const nSimulations = model.results.length;

  const maxMonthlyPayment = Math.max(
    ...model.results.simulations.map(
      (sim) => sim.mortgage.desiredMonthlyPayment,
    ),
  );
  const minMonthlyPayment = Math.min(
    ...model.results.simulations.map(
      (sim) => sim.mortgage.desiredMonthlyPayment,
    ),
  );

  const cheapestSimulation = model.results.simulations.reduce(
    (cheapest, sim) => {
      const simTotalCost = sim.totalCostOfMortgage;
      const cheapestTotalCost = cheapest.totalCostOfMortgage;
      return simTotalCost < cheapestTotalCost ? sim : cheapest;
    },
    model.results.simulations[0],
  );

  // Determine if user provided a fixed term (with calculated interest rate)
  // or a fixed interest rate (with calculated term)
  // If termYears was provided in initial params but interestRate was not, it's fixed-term mode
  const firstSimParams = model.results.simulations[0]?.initialParams;
  const isFixedTermMode =
    firstSimParams?.termYears !== undefined &&
    firstSimParams?.interestRate === undefined;

  // Calculate metrics for each simulation
  const simulationsWithMetrics = model.results.simulations.map((sim) => {
    const monthlyPayment = sim.mortgage.desiredMonthlyPayment;
    const termMonths = sim.mortgage.termMonths;
    const interestRate = sim.mortgage.interestRate;
    const totalPaid = monthlyPayment * termMonths;
    const savingsPercentage = (
      (sim.totalInterestEarned / sim.totalInterestPaid) *
      100
    ).toFixed(1);
    return {
      sim,
      monthlyPayment,
      termMonths,
      interestRate,
      totalPaid,
      savingsPercentage,
    };
  });

  // Sort by monthly payment (lowest first)
  const sortedByPayment = [...simulationsWithMetrics].sort(
    (a, b) => a.monthlyPayment - b.monthlyPayment,
  );

  const lowestPayment = sortedByPayment[0];
  const highestPayment = sortedByPayment[sortedByPayment.length - 1];

  // Calculate differences
  const termDifferenceMonths =
    lowestPayment.termMonths - highestPayment.termMonths;
  const paymentDifference =
    highestPayment.monthlyPayment - lowestPayment.monthlyPayment;
  const costSavings =
    lowestPayment.sim.totalCostOfMortgage -
    highestPayment.sim.totalCostOfMortgage;
  const interestRateDifference =
    highestPayment.interestRate - lowestPayment.interestRate;

  // Render different insights based on the mode
  const renderModeSpecificInsight = () => {
    if (isFixedTermMode) {
      // Fixed term mode: higher payments allow higher interest rates
      return (
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          Paying <strong>{c(paymentDifference)}/month</strong> more supports an
          interest rate of <strong>{interestRateDifference.toFixed(2)}%</strong>{" "}
          higher ({lowestPayment.interestRate.toFixed(2)}% →{" "}
          {highestPayment.interestRate.toFixed(2)}%) while maintaining the same{" "}
          <strong>{yearsAndMonths(lowestPayment.termMonths)}</strong> term.
        </Typography>
      );
    } else {
      // Fixed interest rate mode: higher payments reduce term
      return (
        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
          Paying <strong>{c(paymentDifference)}/month</strong> more reduces the
          term by <strong>{yearsAndMonths(termDifferenceMonths)}</strong> and
          saves <strong>{c(costSavings)}</strong> overall.
        </Typography>
      );
    }
  };

  return (
    <ModeInsightsCard>
      <Typography variant="body2" gutterBottom>
        <strong>{nSimulations}</strong> simulations were compared with monthly
        payments ranging from <strong>{c(minMonthlyPayment)}</strong> to{" "}
        <strong>{c(maxMonthlyPayment)}</strong>.
      </Typography>
      <Typography variant="body2" gutterBottom>
        The most cost-effective monthly payment was{" "}
        <strong>{c(cheapestSimulation.mortgage.desiredMonthlyPayment)}</strong>{" "}
        with a total cost of{" "}
        <strong>{c(cheapestSimulation.totalCostOfMortgage)}</strong>.
      </Typography>
      {renderModeSpecificInsight()}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Monthly Payment</strong>
              </TableCell>
              {isFixedTermMode ? (
                <TableCell align="right">
                  <strong>Interest Rate</strong>
                </TableCell>
              ) : (
                <TableCell align="right">
                  <strong>Term Length</strong>
                </TableCell>
              )}
              <TableCell align="right">
                <strong>Total Cost</strong>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Total amount paid over the life of the mortgage (principal + interest)">
                  <strong>Total Paid</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <strong>Total Interest</strong>
              </TableCell>
              {model.results.simulations.some(
                (sim) => sim.totalInterestEarned > 0,
              ) && (
                <TableCell align="right">
                  <Tooltip title="% of mortgage interest offset by savings & investments interest">
                    <strong>Savings Offset</strong>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedByPayment.map(
              ({
                sim,
                monthlyPayment,
                termMonths,
                interestRate,
                totalPaid,
                savingsPercentage,
              }) => (
                <TableRow
                  key={monthlyPayment}
                  sx={{
                    backgroundColor:
                      sim === cheapestSimulation
                        ? "success.lighter"
                        : sim === lowestPayment.sim
                          ? "error.lighter"
                          : "inherit",
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {c(monthlyPayment)}
                    {sim === cheapestSimulation && <StarIcon />}
                  </TableCell>
                  <TableCell align="right">
                    {isFixedTermMode
                      ? `${interestRate.toFixed(2)}%`
                      : yearsAndMonths(termMonths)}
                  </TableCell>
                  <TableCell align="right">
                    {c(sim.totalCostOfMortgage)}
                  </TableCell>
                  <TableCell align="right">{c(totalPaid)}</TableCell>
                  <TableCell align="right">
                    {c(sim.totalInterestPaid)}
                  </TableCell>
                  {model.results.simulations.some(
                    (s) => s.totalInterestEarned > 0,
                  ) && (
                    <TableCell align="right">
                      {sim.totalInterestEarned > 0
                        ? `${savingsPercentage}%`
                        : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </ModeInsightsCard>
  );
}
